import { FC } from 'react'
import { ChevronDown, ChevronRight, Check, Info } from 'lucide-react'
import { cn } from 'design-system'
import { TreeItemProps } from './types'
import { HighlightedText } from './highlighted-text'
import { InfoTooltip } from 'src/components/points/info-tooltip'

const EXPIRED_CONTRACT_TOOLTIP =
  "This organization's Yellowdig access has ended, so new communities can't be created or transferred in this organization. If you think this is an error, contact support@yellowdig.com."

export const TreeItem: FC<TreeItemProps> = ({
  node,
  onClick,
  level,
  hasChildren,
  isExpanded,
  onToggle,
  isHighlighted,
  isSelected,
  isDisabled,
  isContractExpired,
  searchTerm
}) => {
  return (
    <div
      id={node.network.id}
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-selected={isSelected}
      aria-disabled={isDisabled}
      aria-describedby={
        isDisabled ? `${node.network.id}-disabled-description` : undefined
      }
      data-node-id={node.network.id}
      style={{ marginLeft: `${level * 20}px` }}
      onClick={isDisabled ? undefined : onClick}
      className={cn(
        'relative flex shrink-0 select-none items-center justify-between gap-x-2 rounded-lg p-2 text-sm font-normal outline-none transition-colors',
        {
          'cursor-pointer hover:bg-gray-100': !isDisabled,
          'bg-blue-100': !isDisabled && isSelected,
          'bg-gray-100': !isDisabled && isHighlighted && !isSelected,
          'cursor-not-allowed': isDisabled
        }
      )}>
      {/* apply opacity-50 only to the label content, not the whole row */}
      <div
        className={cn('flex min-w-0 items-center gap-x-2', {
          'text-gray-400 opacity-50': isDisabled
        })}>
        <div className="relative">
          {hasChildren ? (
            <span
              onClick={
                isDisabled
                  ? undefined
                  : e => {
                      e.preventDefault()
                      e.stopPropagation()
                      onToggle(e)
                    }
              }
              className={cn(
                'relative z-10 inline-flex shrink-0 items-center justify-center rounded p-1 text-sm font-normal transition-colors',
                {
                  'cursor-pointer hover:bg-gray-200': !isDisabled,
                  'cursor-not-allowed': isDisabled
                }
              )}>
              {isExpanded ? (
                <ChevronDown size={16} aria-hidden />
              ) : (
                <ChevronRight size={16} aria-hidden />
              )}
            </span>
          ) : null}
          {!hasChildren && <div className="w-6 shrink-0" />}
        </div>
        <div className="truncate">
          <HighlightedText
            text={node.network.title}
            highlight={searchTerm || ''}
          />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-x-1">
        {isContractExpired && (
          <InfoTooltip
            label="Organization access ended"
            Icon={Info}
            tabIndex={-1}>
            {EXPIRED_CONTRACT_TOOLTIP}
          </InfoTooltip>
        )}
        {isSelected && !isDisabled ? (
          <Check size={16} className="shrink-0 text-blue-600" aria-hidden />
        ) : null}
      </div>

      {/* sr-only description for ALL disabled nodes including contract expired —
          wired via aria-describedby so AT announces the full reason */}
      {isDisabled && (
        <span
          id={`${node.network.id}-disabled-description`}
          className="sr-only">
          {isContractExpired
            ? EXPIRED_CONTRACT_TOOLTIP
            : 'This organization is not accessible'}
        </span>
      )}
    </div>
  )
}
